import React from "react"
import { formatCurrencyInThousands, formatPercent } from "./tableFormatters"
import {
  ToolTable,
  ToolTableHeader,
  ToolTableBody,
  ToolTableRow,
  ToolTableHead,
  ToolTableCell,
} from "@/ui/ToolTable"
import { GeographicExposureData } from "~/api/quarterly_reports"

export const GeographicExposureTable = ({
  data,
}: {
  data: GeographicExposureData[]
}) => {
  return (
    <ToolTable>
      <ToolTableHeader>
        <ToolTableRow>
          <ToolTableHead>Geographic Region</ToolTableHead>
          <ToolTableHead>QPCSP Weighting</ToolTableHead>
          <ToolTableHead>Private Equity</ToolTableHead>
          <ToolTableHead>Venture Capital</ToolTableHead>
          <ToolTableHead>Real Assets</ToolTableHead>
          <ToolTableHead>Private Credit</ToolTableHead>
        </ToolTableRow>
      </ToolTableHeader>
      <ToolTableBody>
        {data.map((row, index) => (
          <ToolTableRow
            key={index}
            variant={row.is_total ? "totals" : "default"}
          >
            <ToolTableCell>{row.geographic_region}</ToolTableCell>
            <ToolTableCell>{formatPercent(row.qpcsp_weighting)}</ToolTableCell>
            <ToolTableCell>
              {formatCurrencyInThousands(row.equity)}
            </ToolTableCell>
            <ToolTableCell>
              {formatCurrencyInThousands(row.venture_capital)}
            </ToolTableCell>
            <ToolTableCell>
              {formatCurrencyInThousands(row.real_assets)}
            </ToolTableCell>
            <ToolTableCell>
              {formatCurrencyInThousands(row.credit)}
            </ToolTableCell>
          </ToolTableRow>
        ))}
      </ToolTableBody>
    </ToolTable>
  )
}

const EmptyTable = () => {
  return (
    <div className="editorjs-empty-tool-table">No table data available</div>
  )
}

const hasValidTableData = (data: any) => {
  return data && Array.isArray(data) && data.length > 0
}

export class GeographicExposureTableTool {
  api: any
  block: any
  createPortal: any
  removePortal: any
  portal: any
  tableData: any
  headerInserted: boolean

  constructor(toolConfig: any) {
    this.block = toolConfig.block
    this.api = toolConfig.api
    this.createPortal = toolConfig.config?.createPortal
    this.removePortal = toolConfig.config?.removePortal
    this.tableData = toolConfig.config?.tableData

    this.headerInserted = toolConfig.data?.header_inserted || false

    return this
  }

  static get toolbox() {
    return {
      title: "Geographic Exposure",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
    }
  }

  render() {
    const rootEl = document.createElement("div")
    rootEl.className = "geographic-exposure-table-block"

    const hasData = hasValidTableData(this.tableData)

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    this.portal = this.createPortal(
      this.block.id,
      hasData ? (
        <GeographicExposureTable data={this.tableData} />
      ) : (
        <EmptyTable />
      ),
      rootEl,
    )

    return rootEl
  }

  async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "Geographic Exposure" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current table block
      this.api.blocks.insert(
        "header",
        {
          text: "Geographic Exposure",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the table block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  remove() {
    const totalBlocks = this.api.blocks.getBlocksCount()
    for (let i = 0; i < totalBlocks; i++) {
      const block = this.api.blocks.getBlockByIndex(i)
      if (block && block.id === this.block.id) {
        this.api.blocks.delete(i)
        break
      }
    }
  }

  removed() {
    this.removePortal(this.block.id)
  }

  save() {
    return {
      type: "geographic_exposure_table",
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}
